import React, { useMemo, useState } from 'react';
import Carousel from 'react-material-ui-carousel';
import Typography from '@/components/atoms/typography';
import Image from '@/components/atoms/Image';
import Overlay from '@/components/atoms/overlay';
import usePreloadImages from '@/helpers/hooks/usePreloadImages';
import { StaticImageData } from 'next/image';
import { Reference } from '@/types/lib/reference';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';
import { tablet } from '@/helpers/utils/screensizes';
import useWindowWidth from '@/helpers/hooks/useBrowerWidth';

export interface HeroProps {
  data: any;
}

interface Slide {
  ctas: any;
    banner_desktop_image: { url: string | StaticImageData | undefined; title: string | undefined; description: string | undefined };
  banner_heading: string | undefined;
  banner_description: string | undefined;
  call_to_action: { title: string | undefined; href: string | Reference | undefined };
}

// Define the type for the custom indicators props
interface CustomIndicatorsProps {
  index: number;
  length: number;
  onIndicatorClick: (i: number) => void;
  data: any;
}

const CustomIndicators: React.FC<CustomIndicatorsProps> = ({ index, length, onIndicatorClick, data }) => {
  const width = useWindowWidth();
  const bannerSliderLength = Array.isArray(data?.banner_slider) ? data.banner_slider.length : 0;

  return (
    <div className={` ${bannerSliderLength > 1 ? 'custom-indicator-container' : 'hidden'}`}>
      {Array.from({ length }).map((_, i) => (
        <button
          key={i}
          className={`custom-indicator ${index === i ? 'custom-indicator-active' : ''}`}
          onClick={() => onIndicatorClick(i)} // Call the click handler
          aria-label={`Slide ${i + 1}`} // Accessibility feature
        >
          {/* Place your custom SVG icon here */}
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0 0 48 48">
            <path d="M36.5,42h-25C8.467,42,6,39.532,6,36.5v-25C6,8.468,8.467,6,11.5,6h25c3.033,0,5.5,2.468,5.5,5.5v25C42,39.532,39.533,42,36.5,42z"></path>
          </svg>
        </button>
      ))}
    </div>
  );
};

const Hero: React.FC<HeroProps> = ({ data }) => {
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTabletSize] = useMediaQuery(tablet);

  // Ensure banner_slider exists and is an array
  const bannerSlider = Array.isArray(data?.banner_slider) ? data.banner_slider : [];

  // Preload images with null safety
  const imagesToPreload = [
    bannerSlider[0]?.banner_desktop_image?.url,
    bannerSlider[1]?.banner_desktop_image?.url,
  ].filter(Boolean); // Remove undefined values

  usePreloadImages(imagesToPreload);

  // Function to handle slide change via custom indicators
  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
  };

  // Safe CSS class extraction
  const cmsCssClass = bannerSlider.find((css: { additional_css: any }) => css?.additional_css)?.additional_css;

  // Early return if no slides available
  if (bannerSlider.length === 0) {
    return null;
  }

  return (
    <>
      {loading && <Overlay />}
            <div className={`hero-slider-container relative w-full `}>
        {/* @ts-ignore - Type compatibility issue with Carousel component */}
        <Carousel
          autoPlay={true}
          indicators={false}
          navButtonsAlwaysVisible={isTabletSize && bannerSlider.length > 1}
          navButtonsAlwaysInvisible={true}
          onChange={(index) => setCurrentIndex(index !== undefined ? index : 0)}
          navButtonsProps={{
            className: 'nav-button',
          }}
          duration={8000}
          interval={8000}
        >
          {bannerSlider.map((slide: Slide, index: number) => (
            <div key={index} className={`slide ${currentIndex === index ? 'is-active' : ''}`}>
                            <div className=" relative hero-slider-sm hero-slider-md">
                <picture>
                  <source media="(max-width:699px)" srcSet={slide?.banner_desktop_image?.url || ''} />
                  <source media="(max-width:640px)" srcSet={slide?.banner_desktop_image?.url || ''} />
                  <Image
                    priority
                    loading="eager"
                    src={slide?.banner_desktop_image?.url || '/placeholder-image.jpg'}
                    alt={slide?.banner_desktop_image?.description || slide?.banner_heading || 'Hero image'}
                    fill
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                      maxHeight: '350px',
                    }}
                    className=""
                    onLoad={() => setLoading(false)}
                    height={300}
                    width={500}
                    sizes="(max-width: 468px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </picture>
                <div className="absolute inset-0 bg-black/40 z-10" />
              </div>
              <div className="absolute right-[6%] top-1/2 z-20 flex max-w-[720px] -translate-y-1/2 flex-col items-center text-center px-4 md:items-end md:text-right">
  
  {/* HEADING */}
  <div>
    <Typography
      as="h1"
      className="
        lineHeight-xl lineHeight-2xl
        text-[28px] md:text-[48px] lg:text-[62px]
        font-semibold text-white
        whitespace-normal
      "
    >
      {slide?.banner_heading}
    </Typography>
  </div>

  {/* DESCRIPTION */}
  <div className="mt-[20px] w-full max-w-[600px] lg:mt-[25px]">
    <Typography
      className="
        text-[13px] md:text-[16px] lg:text-[18px]
        font-medium tracking-wide text-white opacity-90
      "
    >
      {slide?.banner_description}
    </Typography>
  </div>
</div>
            </div>
          ))}
        </Carousel>
        <CustomIndicators
          index={currentIndex}
          length={bannerSlider.length}
          onIndicatorClick={handleIndicatorClick}
          data={data}
        />
      </div>
    </>
  );
};

export default Hero;
